// Lazy wrapper around mixpanel-browser. The underlying SDK is fetched in its
// own async chunk on first use (or when the browser goes idle, whichever
// comes first), keeping it out of the main entry bundle.
//
// All calls return void and queue against the load promise — analytics is
// fire-and-forget. Calls dispatched before the SDK finishes loading run in
// registration order once it does, so init/set_config/register_once at app
// startup remain correctly ordered.

type MixpanelModule = (typeof import("mixpanel-browser"))["default"];

let loadPromise: Promise<MixpanelModule> | null = null;

function load(): Promise<MixpanelModule> {
  if (!loadPromise) {
    loadPromise = import("mixpanel-browser").then(m => m.default);
  }
  return loadPromise;
}

// Kick off the import once the browser is idle so the chunk is warm by the
// time the first event fires, without blocking initial render.
if (typeof window !== "undefined") {
  const schedule =
    (window as Window & { requestIdleCallback?: (cb: () => void) => void })
      .requestIdleCallback ?? ((cb: () => void) => setTimeout(cb, 1));
  schedule(() => {
    load();
  });
}

function defer(fn: (m: MixpanelModule) => unknown): void {
  load().then(fn);
}

const mixpanel = {
  init: (
    token: string,
    config?: Parameters<MixpanelModule["init"]>[1],
    name?: string,
  ): void => {
    defer(m => {
      if (config !== undefined && name !== undefined)
        m.init(token, config, name);
      else if (config !== undefined) m.init(token, config);
      else m.init(token);
    });
  },
  track: (event: string, properties?: Record<string, unknown>): void => {
    defer(m => m.track(event, properties));
  },
  identify: (id: string): void => {
    defer(m => m.identify(id));
  },
  reset: (): void => {
    defer(m => m.reset());
  },
  set_config: (config: Parameters<MixpanelModule["set_config"]>[0]): void => {
    defer(m => m.set_config(config));
  },
  register: (props: Record<string, unknown>): void => {
    defer(m => m.register(props));
  },
  register_once: (props: Record<string, unknown>): void => {
    defer(m => m.register_once(props));
  },
  people: {
    set: (props: Record<string, unknown>): void => {
      defer(m => m.people.set(props));
    },
    set_once: (props: Record<string, unknown>): void => {
      defer(m => m.people.set_once(props));
    },
  },
};

export default mixpanel;
